import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { 
  User, 
  Mail, 
  Phone, 
  Calendar,
  Edit,
  Shield,
  Users
} from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { useFamily } from '../hooks/useFamily'
import { useNotifications } from '../contexts/NotificationContext'
import EditProfileModal from '../components/EditProfileModal'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'

const Profile = () => {
  const { user, profile } = useAuth()
  const { family, familyMembers } = useFamily()
  const { addNotification } = useNotifications()
  const [showEditModal, setShowEditModal] = useState(false)

  const getRoleIcon = (role) => {
    return role === 'parent' ? <Shield className="h-4 w-4" /> : <Users className="h-4 w-4" />
  }

  const getRoleLabel = (role) => {
    const roleLabels = {
      'admin': 'Administrador',
      'filho': 'Filho(a)',
      'mae': 'Mãe',
      'pai': 'Pai',
      'avo': 'Avô/Avó',
      'irmao': 'Irmão/Irmã',
      'tio': 'Tio/Tia',
      'primo': 'Primo/Prima',
      'sobrinho': 'Sobrinho/Sobrinha',
      'outro': 'Outro'
    }
    return roleLabels[role] || 'Membro'
  }

  const handleProfileSave = (updatedData) => {
    addNotification({
      type: 'success',
      message: 'Perfil atualizado com sucesso!'
    })
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Meu Perfil</h1>
          <p className="text-neutral-600 mt-1">
            Gerencie suas informações pessoais e configurações
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <button
            onClick={() => setShowEditModal(true)}
            className="btn-primary flex items-center space-x-2"
          >
            <Edit className="h-4 w-4" />
            <span>Editar Perfil</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="lg:col-span-1"
        >
          <div className="bg-white rounded-xl shadow-sm border border-neutral-200 p-6">
            {/* Avatar */}
            <div className="text-center mb-6">
              <div className="relative inline-block">
                <div className="h-24 w-24 bg-primary-500 rounded-full flex items-center justify-center mx-auto mb-3">
                  {profile?.avatar_url ? (
                    <img
                      src={profile.avatar_url}
                      alt="Avatar"
                      className="h-24 w-24 rounded-full object-cover"
                    />
                  ) : (
                    <User className="h-12 w-12 text-white" />
                  )}
                </div>
              </div>
              
              <h2 className="text-xl font-semibold text-neutral-900">
                {profile?.full_name || 'Usuário'}
              </h2>
              <p className="text-neutral-500">
                {profile?.role && getRoleLabel(profile.role)}
              </p>
            </div>

            {/* Family Info */}
            {family && (
              <div className="border-t border-neutral-200 pt-4">
                <h3 className="font-medium text-neutral-900 mb-2">Família</h3>
                <p className="text-sm text-neutral-600">{family.name}</p>
                <p className="text-xs text-neutral-500 mt-1">
                  {familyMembers.length} membro(s)
                </p>
              </div>
            )}

            {/* Stats */}
            <div className="border-t border-neutral-200 pt-4 mt-4">
              <h3 className="font-medium text-neutral-900 mb-3">Estatísticas</h3>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-neutral-600">Membro desde</span>
                  <span className="font-medium">
                    {profile?.created_at && 
                      format(new Date(profile.created_at), 'MMM yyyy', { locale: ptBR })
                    }
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-neutral-600">Tarefas concluídas</span>
                  <span className="font-medium">-</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-neutral-600">Pontos ganhos</span>
                  <span className="font-medium">-</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Profile Details */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="lg:col-span-2"
        >
          <div className="bg-white rounded-xl shadow-sm border border-neutral-200 p-6">
            <h3 className="text-lg font-semibold text-neutral-900 mb-6">
              Informações Pessoais
            </h3>

            <div className="space-y-6">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Nome Completo
                </label>
                <p className="text-neutral-900 py-2">
                  {profile?.full_name || 'Não informado'}
                </p>
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  E-mail
                </label>
                <div className="flex items-center space-x-2">
                  <Mail className="h-4 w-4 text-neutral-400" />
                  <p className="text-neutral-900 py-2">{user?.email}</p>
                </div>
                <p className="text-xs text-neutral-500 mt-1">
                  E-mail não pode ser alterado
                </p>
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Telefone
                </label>
                <div className="flex items-center space-x-2">
                  <Phone className="h-4 w-4 text-neutral-400" />
                  <p className="text-neutral-900 py-2">
                    {profile?.phone || 'Não informado'}
                  </p>
                </div>
              </div>

              {/* Bio */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Biografia
                </label>
                <p className="text-neutral-900 py-2">
                  {profile?.bio || 'Nenhuma biografia adicionada'}
                </p>
              </div>

              {/* Role */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Função na Família
                </label>
                <div className="flex items-center space-x-2">
                  {profile?.role === 'admin' ? (
                    <Shield className="h-4 w-4 text-blue-600" />
                  ) : (
                    <Users className="h-4 w-4 text-neutral-400" />
                  )}
                  <p className="text-neutral-900 py-2">
                    {profile?.role && getRoleLabel(profile.role)}
                  </p>
                </div>
                <p className="text-xs text-neutral-500 mt-1">
                  {profile?.role === 'admin' 
                    ? 'Você é administrador da família'
                    : 'Função pode ser alterada pelo administrador'
                  }
                </p>
              </div>

              {/* Member Since */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Membro desde
                </label>
                <div className="flex items-center space-x-2">
                  <Calendar className="h-4 w-4 text-neutral-400" />
                  <p className="text-neutral-900 py-2">
                    {profile?.created_at && 
                      format(new Date(profile.created_at), 'dd/MM/yyyy', { locale: ptBR })
                    }
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Settings */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mt-6 bg-white rounded-xl shadow-sm border border-neutral-200 p-6"
          >
            <h3 className="text-lg font-semibold text-neutral-900 mb-4">
              Configurações
            </h3>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-neutral-900">Notificações por e-mail</p>
                  <p className="text-sm text-neutral-500">
                    Receber lembretes e atualizações
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" className="sr-only peer" defaultChecked />
                  <div className="w-11 h-6 bg-neutral-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-neutral-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                </label>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-neutral-900">Modo escuro</p>
                  <p className="text-sm text-neutral-500">
                    Usar tema escuro na interface
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" className="sr-only peer" />
                  <div className="w-11 h-6 bg-neutral-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-neutral-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                </label>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Edit Profile Modal */}
      <EditProfileModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSave={handleProfileSave}
      />
    </div>
  )
}

export default Profile


